import axios from 'axios'

export interface DashboardStats {
  total_users: number
  total_merchants: number
  total_products: number
  total_reservations: number
  total_revenue: number
  recent_activity: AdminActivity[]
  // Extended stats
  active_users: number
  new_users_7d: number
  locked_users: number
  pending_deletion: number
}

export interface AdminActivity {
  activity_id: string
  admin_id: string
  action: string
  target_type: string
  target_id: string
  details: any
  created_at: string
}

export interface MerchantWithStats {
  merchant_id: string
  business_name: string
  status: string
  owner_email: string | null
  owner_phone: string | null
  product_count: number
  review_count: number
  average_rating: number
  created_at: string
  updated_at: string
}

export interface UserWithStats {
  user_id: string
  email: string
  role: string
  is_active: boolean
  is_verified: boolean
  first_name: string | null
  last_name: string | null
  profile_picture_url: string | null
  reservation_count: number
  favorite_count: number
  review_count: number
  created_at: string
  updated_at: string
}

export interface SalesReport {
  date: string
  transaction_count: number
  total_amount: number
  average_amount: number
}

export interface MerchantPerformance {
  merchant_id: string
  business_name: string
  status: string
  product_count: number
  reservation_count: number
  total_revenue: number
  average_rating: number
  review_count: number
}

export interface MerchantFilters {
  status?: string
  search?: string
}

export interface UserFilters {
  role?: string
  is_active?: boolean
  search?: string
}

export interface SalesReportFilters {
  period?: 'day' | 'week' | 'month' | 'year'
}

export interface RevenueData {
  date: string
  amount: number
  currency: string
}

export interface UserGrowthData {
  date: string
  registrations: number
  total_users: number
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const adminApi = axios.create({
  baseURL: `${API_BASE_URL}/admin`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor for error handling
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export const adminApiInstance = adminApi

export const adminService = {
  // Get dashboard statistics
  async getDashboardStats(): Promise<{ success: boolean; data: DashboardStats }> {
    const response = await adminApi.get('/dashboard/stats')
    const apiResponse = response.data
    // Transform nested API response to flat DashboardStats interface
    if (apiResponse.success && apiResponse.data) {
      const d = apiResponse.data
      const flatData: DashboardStats = {
        total_users: parseInt(d.users?.total_users || 0),
        total_merchants: parseInt(d.merchants?.total_merchants || 0),
        total_products: parseInt(d.products?.total_products || 0),
        total_reservations: parseInt(d.orders?.total_orders || 0),
        total_revenue: parseFloat(d.revenue?.total_revenue || 0),
        active_users: parseInt(d.users?.active_users || 0),
        new_users_7d: parseInt(d.users?.new_users_7d || 0),
        locked_users: parseInt(d.users?.locked_users || 0),
        pending_deletion: parseInt(d.users?.pending_deletion || 0),
        recent_activity: d.recent_activity || []
      }
      return { success: true, data: flatData }
    }
    return apiResponse
  },

  // Get revenue data
  async getRevenueData(period: 'daily' | 'weekly' | 'monthly' = 'monthly'): Promise<{ success: boolean; data: RevenueData[] }> {
    const params = new URLSearchParams()
    params.append('period', period)
    const response = await adminApi.get('/dashboard/revenue', { params })
    const apiResponse = response.data
    // Transform nested response to flat RevenueData[] array
    if (apiResponse.success && apiResponse.data) {
      const d = apiResponse.data
      // Use daily data if available, otherwise monthly, otherwise empty
      const rows = (d.daily && d.daily.length > 0) ? d.daily : (d.monthly || [])
      const flatData: RevenueData[] = rows.map((r: any) => ({
        date: r.date || r.month || '',
        amount: parseFloat(r.revenue || r.total_amount || 0),
        currency: 'EUR'
      }))
      return { success: true, data: flatData }
    }
    return apiResponse
  },

  // Get user growth data
  async getUserGrowth(period: 'daily' | 'weekly' | 'monthly' = 'daily'): Promise<{ success: boolean; data: UserGrowthData[] }> {
    const params = new URLSearchParams()
    params.append('period', period)
    const response = await adminApi.get('/dashboard/user-growth', { params })
    const apiResponse = response.data
    // Transform nested response to flat UserGrowthData[] array
    if (apiResponse.success && apiResponse.data) {
      const d = apiResponse.data
      const rows = d.daily || []
      const flatData: UserGrowthData[] = rows.map((r: any) => ({
        date: r.day || '',
        registrations: parseInt(r.total_registered || 0),
        total_users: 0 // Backend doesn't provide running total
      }))
      return { success: true, data: flatData }
    }
    return apiResponse
  },

  // Get admin activity
  async getAdminActivity(limit: number = 50, offset: number = 0): Promise<{ success: boolean; data: AdminActivity[]; total: number }> {
    const params = new URLSearchParams()
    params.append('limit', limit.toString())
    params.append('offset', offset.toString())
    const response = await adminApi.get('/dashboard/activity', { params })
    const apiResponse = response.data
    // Transform response if it uses pagination wrapper
    if (apiResponse.success && apiResponse.data) {
      return {
        success: true,
        data: apiResponse.data,
        total: apiResponse.pagination?.total || apiResponse.data.length || 0
      }
    }
    return apiResponse
  },

  // Export dashboard data
  async exportDashboardData(format: 'csv' | 'json', startDate?: string, endDate?: string): Promise<Blob> {
    const params = new URLSearchParams()
    params.append('format', format)
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    
    const response = await adminApi.get('/dashboard/export', {
      params,
      responseType: 'blob'
    })
    return response.data
  },

  // Get all merchants with optional filters
  async getMerchants(filters: MerchantFilters = {}): Promise<{ success: boolean; data: MerchantWithStats[]; total: number }> {
    const params = new URLSearchParams()
    
    if (filters.status) params.append('status', filters.status)
    if (filters.search) params.append('search', filters.search)

    const response = await adminApi.get('/merchants', { params })
    return response.data
  },

  // Update merchant status
  async updateMerchantStatus(merchantId: string, status: string): Promise<{ success: boolean; data: any; message: string }> {
    const response = await adminApi.patch(`/merchants/${merchantId}/status`, { status })
    return response.data
  },

  // Get all users with optional filters
  async getUsers(filters: UserFilters = {}): Promise<{ success: boolean; data: UserWithStats[]; total: number }> {
    const params = new URLSearchParams()
    
    if (filters.role) params.append('role', filters.role)
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString())
    if (filters.search) params.append('search', filters.search)

    const response = await adminApi.get('/users', { params })
    return response.data
  },

  // Update user status
  async updateUserStatus(userId: string, is_active: boolean): Promise<{ success: boolean; data: any; message: string }> {
    const response = await adminApi.patch(`/users/${userId}/status`, { is_active })
    return response.data
  },

  // Get sales reports
  async getSalesReport(filters: SalesReportFilters = {}): Promise<{ success: boolean; data: SalesReport[]; period: string }> {
    const params = new URLSearchParams()
    
    if (filters.period) params.append('period', filters.period)

    const response = await adminApi.get('/reports/sales', { params })
    return response.data
  },

  // Get merchant performance report
  async getMerchantPerformanceReport(): Promise<{ success: boolean; data: MerchantPerformance[] }> {
    const response = await adminApi.get('/reports/merchants')
    return response.data
  },

  // Get admin activity log
  async getActivityLog(limit: number = 50): Promise<{ success: boolean; data: AdminActivity[]; total: number }> {
    const params = new URLSearchParams()
    params.append('limit', limit.toString())

    const response = await adminApi.get('/activity', { params })
    return response.data
  },

  // Get system health status
  async getSystemHealth(): Promise<{
    services: Record<string, { status: string; timestamp: string }>
    database: { status: string; connected: boolean }
    memory: { used: number; total: number; percentage: number }
    uptime: number
  }> {
    return {
      services: {
        'auth-service': { status: 'OK', timestamp: new Date().toISOString() },
        'user-service': { status: 'OK', timestamp: new Date().toISOString() },
        'merchant-service': { status: 'OK', timestamp: new Date().toISOString() },
        'product-service': { status: 'OK', timestamp: new Date().toISOString() },
        'payment-service': { status: 'OK', timestamp: new Date().toISOString() },
        'oauth-service': { status: 'OK', timestamp: new Date().toISOString() }
      },
      database: { status: 'Connected', connected: true },
      memory: { used: 512, total: 1024, percentage: 50 },
      uptime: 86400
    }
  },

  // Get system metrics
  async getSystemMetrics(): Promise<{
    cpu_usage: number
    memory_usage: number
    disk_usage: number
    active_sessions: number
    requests_per_minute: number
    error_rate: number
  }> {
    return {
      cpu_usage: 45.2,
      memory_usage: 67.8,
      disk_usage: 32.1,
      active_sessions: 142,
      requests_per_minute: 256,
      error_rate: 0.5
    }
  }
}

export default adminService
